/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { PaginationParams, toHttpParams } from '../model/api/pagination-params';
import { IdDataRest, PaginatedPermissions, PermissionData, PermissionTemplatesData, PermittedCommandsData, PermittedInfoCommandsData } from '../model/api/data-rest';
import { resources } from './api';
import { Permission, PermissionTemplate, PermittedCommand } from '../model/permission';
import { environment } from 'src/environments/environment';


@Injectable({
    providedIn: 'root'
})
export class PermissionService {

    constructor(
        private http: HttpClient,
    ) {
    }

    getPermissions(siteId?: number, paging?: PaginationParams, filter?: string, activeFilter?: boolean,
        readyFilter?: boolean, expiredFilter?: boolean, createdFilter?: boolean): Observable<PaginatedPermissions> {
        let params: HttpParams;
        if (paging) {
            params = toHttpParams(paging);
        } else {
            params = new HttpParams();
        }
        if (filter) {
            params = params.append('filter', filter);
        }
        if (activeFilter != null) {
            params = params.append('activeFilter', String(activeFilter));
        }
        if (readyFilter != null) {
            params = params.append('readyFilter', String(readyFilter));
        }
        if (expiredFilter != null) {
            params = params.append('expiredFilter', String(expiredFilter));
        }
        if (createdFilter != null) {
            params = params.append('createdFilter', String(createdFilter));
        }
        if (siteId) {
            params = params.append('siteid', siteId.toString());
        }
        return this.http.get<PaginatedPermissions>(`${environment.apiUrl}${resources.get('permissions')}`, { params });
    }

    getPermissionsCalendar(siteId?: number, datefrom?: string, dateto?: string): Observable<PaginatedPermissions> {
        let params: HttpParams;
        params = new HttpParams();
        if (datefrom) {
            params = params.append('datefrom', datefrom);
        }
        if (dateto) {
            params = params.append('dateto', dateto);
        }
        if (siteId) {
            params = params.append('siteid', siteId.toString());
        }
        return this.http.get<PaginatedPermissions>(`${environment.apiUrl}${resources.get('permissions')}`, { params });
    }

    getPermission(permissionId: number): Observable<PermissionData> {
        return this.http.get<PermissionData>(`${environment.apiUrl}${resources.get('permissions')}/${permissionId}`);
    }

    addPermission(siteId: number, permission: Permission, permittedCommands: PermittedCommand[]): Observable<IdDataRest> {
        const Commands = permittedCommands.map(cmd => ({ CommandId: cmd.CommandId, DeviceId: cmd.DeviceId }));
        return this.http.post<IdDataRest>(`${environment.apiUrl}${resources.get('permissions')}`, { SiteId: siteId, ...permission, Commands });
    }

    editPermission(permission: Permission, permittedCommands?: PermittedCommand[]): Observable<IdDataRest> {
        const { SiteId, Name, Description, Start, End, Tz, CustomerLanguage, RfidQeCode,
            RandomPincode, PinCode, SendPincodeRbe, SendPincodeMtma, SendRfidRbe, SendRfidMtma, SendMail, SendAccessMail } = permission;
        const payload: any = {
            SiteId, Name, Description, Start, End, Tz, CustomerLanguage,
            RfidQeCode, RandomPincode, PinCode, SendPincodeRbe, SendPincodeMtma, SendRfidRbe, SendRfidMtma, SendMail, SendAccessMail
        };

        if (typeof permittedCommands !== 'undefined') {
            payload.Commands = permittedCommands.map(cmd => ({ CommandId: cmd.CommandId, DeviceId: cmd.DeviceId }));
        }
        return this.http.patch<IdDataRest>(`${environment.apiUrl}${resources.get('permissions')}/${permission.Id}`, payload);
    }

    getPermittedCommands(siteId: number): Observable<PermittedCommandsData> {
        return this.http.get<PermittedCommandsData>(
            `${environment.apiUrl}${resources.get('permissions')}${resources.get('sites')}/${siteId}${resources.get('commands')}`
        );
    }

    deletePermission(permissionId: number): Observable<IdDataRest> {
        return this.http.delete<IdDataRest>(`${environment.apiUrl}${resources.get('permissions')}/${permissionId}`);
    }

    getTokenPermissionCommands(token: string): Observable<PermittedInfoCommandsData> {
        return this.http.get<PermittedInfoCommandsData>(
            `${environment.apiUrl}${resources.get('permission')}/${token}${resources.get('info')}`,
        );
    }

    setPin(token: string, pin: string): Observable<IdDataRest> {
        return this.http.post<IdDataRest>(
            `${environment.apiUrl}${resources.get('permission')}/${token}${resources.get('pin')}`,
            { Pin: pin },
        );
    }

    executeCommand(token: string, pin: string, commandId: number): Observable<IdDataRest> {
        return this.http.post<IdDataRest>(
            `${environment.apiUrl}${resources.get('permission')}/${token}${resources.get('execute')}`,
            { Pin: pin, CommandId: commandId },
        );
    }

    resetPin(permissionId: number): Observable<IdDataRest> {
        return this.http.delete<IdDataRest>(`${environment.apiUrl}${resources.get('permissions')}/${permissionId}${resources.get('pin')}`);
    }

    getPermissionTemplates(siteId: number): Observable<PermissionTemplatesData> {
        return this.http.get<PermissionTemplatesData>(
            `${environment.apiUrl}${resources.get('permissions')}${resources.get('sites')}/${siteId}${resources.get('templates')}`
        );
    }

    savePermissionTemplates(siteId: number, template: PermissionTemplate): Observable<IdDataRest> {
        return this.http.post<IdDataRest>(
            `${environment.apiUrl}${resources.get('permissions')}${resources.get('sites')}/${siteId}${resources.get('templates')}`,
            template,
        );
    }

    updatePermissionTemplates(siteId: number, template: PermissionTemplate): Observable<IdDataRest> {
        const payload = { ...template };
        delete payload.Id;
        return this.http.patch<IdDataRest>(
            `${environment.apiUrl}${resources.get('permissions')}${resources.get('sites')}/${siteId}${resources.get('templates')}/${template.Id}`,
            payload,
        );
    }

    resetPinToken(token: string): Observable<IdDataRest> {
        return this.http.delete<IdDataRest>(
            `${environment.apiUrl}${resources.get('permission')}/${token}${resources.get('pin')}`,
            {},
        );
    }

    deletePermissionExpired(siteId: number): Observable<IdDataRest> {
        let params: HttpParams;
        params = new HttpParams();
        if (siteId) {
            params = params.append('siteid', siteId.toString());
        }
        return this.http.delete<IdDataRest>(`${environment.apiUrl}${resources.get('permissions')}${resources.get('expired')}`, { params });
    }
}
